import React from 'react'
// libs
import { useMutation, useQuery } from '@apollo/client/react'
import { useSnackbar } from 'notistack'
// components
import { Button } from 'components/UI'
import { CardContent } from '@mui/material'
import PageLoader from 'components/UI/Loader/PageLoader'
// graphql
import { MY_SESSIONS_QUERY } from 'graphql/auth/queries'
import { AUTH_REVOKE_SESSION_MUTATION, AUTH_SIGN_OUT_ALL_MUTATION } from 'graphql/auth/mutations'
// views
import { ButtonGroup, InfoText, SectionCard, SectionTitle } from '../../../shared/views'
import { CurrentBadge, SessionDetail, SessionDevice, SessionInfo, SessionItem } from './views'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface Session {
  id: string
  ipAddress: string
  deviceLabel: string
  lastActiveAt: string
  createdAt: string
  isCurrent: boolean
}

interface MySessionsResponse {
  mySessions: {
    ok: boolean
    data: {
      sessions: Session[]
    }
  }
}

const SessionList: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const { data, loading, refetch } = useQuery<MySessionsResponse>(MY_SESSIONS_QUERY, {
    fetchPolicy: 'network-only'
  })

  const [revokeSession, { loading: revoking }] = useMutation(AUTH_REVOKE_SESSION_MUTATION)
  const [signOutAll, { loading: signingOutAll }] = useMutation(AUTH_SIGN_OUT_ALL_MUTATION)

  const sessions = data?.mySessions?.data?.sessions || []

  const handleRevoke = async (sessionId: string) => {
    try {
      await revokeSession({ variables: { sessionId } })
      enqueueSnackbar('Session revoked', { variant: 'success' })
      refetch()
    } catch (error) {
      console.error('[SessionList.handleRevoke]: ', parseGraphQLErrorMessages(error))
    }
  }

  const handleSignOutAll = async () => {
    try {
      await signOutAll()
      enqueueSnackbar('All other sessions have been revoked', { variant: 'success' })
      refetch()
    } catch (error) {
      console.error('[SessionList.handleSignOutAll]: ', parseGraphQLErrorMessages(error))
    }
  }

  if (loading) {
    return <PageLoader isLoading height='200px' />
  }

  return (
    <SectionCard>
      <CardContent>
        <SectionTitle>Active Sessions</SectionTitle>
        <InfoText>
          These are the devices currently logged into your account. You can revoke access for any session you don't
          recognize.
        </InfoText>

        {sessions.map((session) => (
          <SessionItem key={session.id}>
            <SessionInfo>
              <SessionDevice>
                {session.deviceLabel || 'Unknown Device'}
                {session.isCurrent && <CurrentBadge>Current</CurrentBadge>}
              </SessionDevice>
              <SessionDetail>IP: {session.ipAddress}</SessionDetail>
              <SessionDetail>Last active: {new Date(session.lastActiveAt).toLocaleString()}</SessionDetail>
              <SessionDetail>Created: {new Date(session.createdAt).toLocaleString()}</SessionDetail>
            </SessionInfo>
            {!session.isCurrent && (
              <Button layout='RED' variant='outlined' onClick={() => handleRevoke(session.id)} disabled={revoking}>
                Revoke
              </Button>
            )}
          </SessionItem>
        ))}

        {sessions.length > 1 && (
          <ButtonGroup>
            <Button variant='outlined' onClick={handleSignOutAll} disabled={signingOutAll}>
              Sign Out All Other Sessions
            </Button>
          </ButtonGroup>
        )}
      </CardContent>
    </SectionCard>
  )
}

export default SessionList
